import { sumDecimal } from '../../components/data-grid/money';

/**
 * The document list filters. `type` and `status` narrow the request to
 * `GET /documents`; the period and the free-text search narrow the loaded
 * rows in the browser because the endpoint has no period parameter.
 */

export type DocumentStatus = 'draft' | 'pending' | 'approved' | 'posted' | 'cancelled';

export type StatusFilter = DocumentStatus | 'all';

export interface DocumentPeriod {
  id: string;
  year: number;
  month: number;
  status: 'open' | 'closing' | 'closed';
  /** The organisation the period belongs to; the document form takes its `orgId` from here. */
  orgId?: string;
}

export interface DocumentLineLike {
  amount: string;
}

export interface DocumentRow {
  id: string;
  number: string;
  type: string;
  typeName: string;
  date: string;
  status: DocumentStatus;
  author: string;
  description: string;
  amount: string;
  lines: DocumentLineLike[];
}

export interface DocumentFilters {
  type: string;
  status: StatusFilter;
  periodId: string;
  orgId: string;
  search: string;
}

export const EMPTY_FILTERS: DocumentFilters = { type: '', status: 'all', periodId: '', orgId: '', search: '' };

function pad2(value: number): string {
  return value.toString().padStart(2, '0');
}

export function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Inclusive ISO range of a calendar month: 2026-02 -> 2026-02-01 .. 2026-02-28. */
export function periodRange(period: DocumentPeriod): { from: string; to: string } {
  const month = pad2(period.month);
  return {
    from: `${period.year}-${month}-01`,
    to: `${period.year}-${month}-${pad2(lastDayOfMonth(period.year, period.month))}`,
  };
}

function contains(haystack: string, needle: string): boolean {
  return haystack.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
}

export function filterDocuments(
  rows: readonly DocumentRow[],
  filters: DocumentFilters,
  periods: readonly DocumentPeriod[],
): DocumentRow[] {
  const period = periods.find((candidate) => candidate.id === filters.periodId);
  const range = period === undefined ? null : periodRange(period);
  const search = filters.search.trim();
  return rows.filter((row) => {
    if (filters.type !== '' && row.type !== filters.type) {
      return false;
    }
    if (filters.status !== 'all' && row.status !== filters.status) {
      return false;
    }
    if (range !== null) {
      const day = row.date.slice(0, 10);
      if (day < range.from || day > range.to) {
        return false;
      }
    }
    if (search !== '' && !(contains(row.number, search) || contains(row.description, search) || contains(row.author, search))) {
      return false;
    }
    return true;
  });
}

export interface Page<T> {
  items: T[];
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
}

/** One-based paging that clamps instead of showing an empty page past the end. */
export function paginate<T>(rows: readonly T[], page: number, pageSize: number): Page<T> {
  const size = Math.max(1, Math.floor(pageSize));
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (current - 1) * size;
  const items = rows.slice(start, start + size);
  return {
    items: [...items],
    page: current,
    pageCount,
    total,
    from: items.length === 0 ? 0 : start + 1,
    to: start + items.length,
  };
}

/** A document's amount is the exact sum of its line amounts, at the widest scale. */
export function documentTotal(document: { lines: readonly DocumentLineLike[] }): string {
  return sumDecimal(document.lines.map((line) => line.amount));
}

export function statusKey(status: DocumentStatus): string {
  return `documentsStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

export function monthKey(month: number): string {
  return `documentsMonth${month}`;
}
